import { useTranslation } from 'react-i18next';

import type { CadeCaseRegister } from '@/services/types';

/**
 * One matter's dated milestones from CADE's published register.
 *
 * The register is a different population from the session archive the rest of
 * this page describes: it dates the filing, the SG's decision, the Tribunal's
 * decision and the trânsito em julgado to the DAY, where the archive carries only
 * a folder year. The two are shown side by side by name and never merged -- the
 * span in days here is on the register's basis, and the "years to last session"
 * above it is on the archive's.
 *
 * The register's own words for each decision are shown as written (`Condenação`,
 * `Homologação`): a translation would be this product's reading of CADE's
 * classification, and the classification is the thing a lawyer cites.
 *
 * Dates are formatted in UTC. They are calendar dates with no time of day, and a
 * local-zone format would show the previous day to a viewer west of Greenwich.
 */
export function RegisterTimeline({ register }: { register: CadeCaseRegister }) {
  const { t, i18n } = useTranslation(['cade']);
  const day = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' });
  const money = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const years = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });

  return (
    <div className="space-y-3" data-register-timeline>
      {register.events.length > 0 ? (
        <ol className="space-y-1.5 border-l pl-4 text-sm">
          {register.events.map((event) => (
            <li key={`${event.kind}-${event.when}-${event.outcome ?? ''}-${event.session_number ?? ''}`}>
              <span className="tabular-nums text-foreground">
                {day.format(new Date(`${event.when}T00:00:00Z`))}
              </span>
              <span className="ml-2 text-muted-foreground">
                {t(`cade:case.register.kinds.${event.kind}`)}
              </span>
              {event.outcome ? (
                <span className="ml-2 font-medium text-foreground">{event.outcome}</span>
              ) : null}
              {event.session_number ? (
                <span className="ml-2 text-xs text-muted-foreground">
                  {t('cade:case.register.session', { number: event.session_number })}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-xs text-muted-foreground">{t('cade:case.register.noDates')}</p>
      )}

      {register.filing_to_decision_days !== null && register.filing_to_decision_kind ? (
        <p className="text-xs text-muted-foreground" data-register-span>
          {t('cade:case.register.span', {
            days: number.format(register.filing_to_decision_days),
            years: years.format(register.filing_to_decision_days / 365.25),
            kind: t(`cade:case.register.spanTo.${register.filing_to_decision_kind}`),
          })}
        </p>
      ) : null}

      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
        {register.procedure ? <Field label={t('cade:case.register.fields.procedure')} value={register.procedure} /> : null}
        {register.track ? <Field label={t('cade:case.register.fields.track')} value={register.track} /> : null}
        {register.decided_by ? <Field label={t('cade:case.register.fields.decidedBy')} value={register.decided_by} /> : null}
        {register.rapporteur ? <Field label={t('cade:case.register.fields.rapporteur')} value={register.rapporteur} /> : null}
        {register.appeal ? (
          <Field
            label={t('cade:case.register.fields.appeal')}
            value={register.appeal_type ? `${register.appeal} · ${register.appeal_type}` : register.appeal}
          />
        ) : null}
        {register.operation_value_brl !== null ? (
          <Field label={t('cade:case.register.fields.operation')} value={money.format(register.operation_value_brl)} />
        ) : null}
      </dl>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}
